'use client'

import { useCallback, useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'

/**
 * 顯現動畫（kiao-voice-mono.html 的 .reveal 行為）。
 *
 * 把回傳的 ref 掛在帶有 `reveal` class 的元素上：進入視窗
 * （threshold 0.12）時標記 `data-in`，之後解除觀察。
 * 用 data attribute 而非 classList，因為 React 重新 render 時會整串
 * 覆寫 className（如示範卡的 phase class），imperative 加的 class
 * 會被洗掉；React 不會動它沒 render 的 attribute。
 * 無 IntersectionObserver 的環境直接顯示；prefers-reduced-motion
 * 由 CSS 處理（直接顯示、無轉場）。
 */
export function useReveal() {
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [])

  return useCallback((element: HTMLElement | null) => {
    if (!element) return
    if (typeof IntersectionObserver === 'undefined') {
      element.setAttribute('data-in', '')
      return
    }
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.setAttribute('data-in', '')
              observer.unobserve(entry.target)
            }
          }
        },
        { threshold: 0.12 }
      )
    }
    observerRef.current.observe(element)
  }, [])
}

/** reveal 的階梯延遲（--rd），對應 prototype 的 style="--rd:90ms"。 */
export function revealDelay(ms: number): CSSProperties {
  return { '--rd': `${ms}ms` } as CSSProperties
}
